import { useContext, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import t from 'react-translate';
import { validateZoomHosts } from 'redux/actions/live-events';
import { useAppDispatch } from 'redux/store';
import { Props as NvTextInputProps } from 'shared/components/inputs/nv-text-input';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { LiveSessionFormContext } from './live-session-form';
import HostValidator from './host-validator';

type ZoomHostValidatorProps = Omit<NvTextInputProps, 'withForm' | 'name'> & {
  formName: string;
  disabled: boolean;
  host?: string;
  isAlternativeHost?: boolean;
  onValidation?: (isValid: boolean, value: string) => void;
};

const ZoomHostValidator = (props: ZoomHostValidatorProps) => {
  const {
    formName,
    isAlternativeHost,
    onValidation,
    host,
    ...restProps
  } = props;

  const dispatch = useAppDispatch();
  const { catalogId, showZoomHostValidator } = useContext(LiveSessionFormContext);
  const { watch } = useFormContext();

  // Track validity internally for main host to control visibility
  const [isValidHost, setIsValidHost] = useState(() => !isAlternativeHost && !!host);

  // For main host: don't render if neither showZoomHostValidator nor isValidHost
  if (!isAlternativeHost && !showZoomHostValidator() && !isValidHost) {
    return null;
  }

  const hostValue = watch(formName);
  const handleValidHost = (isValid: boolean) => {
    if (!isAlternativeHost) {
      setIsValidHost(isValid);
    }
    onValidation?.(isValid, hostValue);
  };

  const handleValidate = async (hostUserIds: string) => {
    const result = await dispatch(validateZoomHosts({ catalogId, hostUserIds })).unwrap();
    return result as { isValid: string[]; isInvalid: string[] };
  };

  const handleValidationError = (invalidHosts: string[]) => {
    const messageInput = invalidHosts.length > 1 ? invalidHosts.join(',') : invalidHosts[0];
    let message: string;
    if (isAlternativeHost) {
      message = invalidHosts.length > 1
        ? t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ZOOM_HOST_ERROR.ACCOUNT_LEVEL.MULTIPLE(messageInput)
        : t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ZOOM_HOST_ERROR.ACCOUNT_LEVEL.SINGLE(messageInput);
    } else {
      message = t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ZOOM_HOST_ERROR.ACCOUNT_LEVEL.HOST();
    }
    dispatch(addAlertMessage({
      type: AlertMessageType.ERROR,
      header: t.FORM.OOPS(),
      message,
    }));
  };

  const label = isAlternativeHost
    ? t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ALTERNATIVE_HOSTS()
    : t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ZOOM_MEETING_HOST();

  const placeholder = isAlternativeHost
    ? t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ALTERNATIVE_HOSTS_EXAMPLE()
    : t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ZOOM_MEETING_HOST();

  const description = isAlternativeHost
    ? t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ALTERNATIVE_HOSTS_DESCRIPTION()
    : undefined;

  return (
    <HostValidator
      {...restProps}
      formName={formName}
      host={host}
      isMultipleHosts={isAlternativeHost}
      onValidHost={handleValidHost}
      onValidate={handleValidate}
      onValidationError={handleValidationError}
      showValidator={showZoomHostValidator()}
      label={label}
      placeholder={placeholder}
      description={description}
    />
  );
};

export default ZoomHostValidator;
